import React, { useEffect, useState } from 'react';

interface Props {
  src?: string | null;
  name?: string | null;
  username?: string | null;
  size?: number;
  className?: string;
  rounded?: boolean;
  alt?: string;
  title?: string;
}

const COLORS = [
  'bg-blue-500',
  'bg-emerald-500',
  'bg-purple-500',
  'bg-pink-500',
  'bg-amber-500',
  'bg-teal-500',
  'bg-rose-500',
  'bg-indigo-500',
];

function hashString(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
  }
  return hash;
}

function getInitials(name?: string | null, username?: string | null): string {
  const base = (name || username || '').trim();
  if (!base) {
    return '?';
  }
  // Split on whitespace AND common username separators (`_`, `-`, `.`) so
  // that snake/kebab/dot handles like `jane_smith` or `jane.smith` render
  // as `JS` instead of `JA`.
  const parts = base.split(/[\s_\-.]+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
  }
  return base.slice(0, 2).toUpperCase();
}

/**
 * Account / profile avatar with graceful fallback.
 *
 * Renders `src` when loadable. If `src` is missing, empty, or the image
 * fails to load (broken CDN links, expired social-profile pictures, etc.)
 * it falls back to a colored circle with initials derived from `name`
 * or `username`. Intended to be used across the system for any human or
 * account avatar surface.
 */
export const AccountAvatar: React.FC<Props> = ({
  src,
  name,
  username,
  size = 32,
  className = '',
  rounded = true,
  alt,
  title,
}) => {
  const [failed, setFailed] = useState(false);

  // Reset failure state when the src changes.
  useEffect(() => {
    setFailed(false);
  }, [src]);

  const usable = Boolean(src && !failed);
  const initials = getInitials(name, username);
  const colorKey = (name || username || 'anon').toLowerCase();
  const color = COLORS[hashString(colorKey) % COLORS.length];
  const radius = rounded ? 'rounded-full' : 'rounded-md';
  const fontSize = Math.max(10, Math.floor(size * 0.4));

  const style: React.CSSProperties = { width: size, height: size };

  if (usable) {
    // Default alt to 'Avatar' (not '') so the <img> keeps the implicit
    // role="img" for assistive tech and `screen.getByRole('img')`.
    return (
      <img
        src={src || ''}
        alt={alt ?? (name || username || 'Avatar')}
        title={title}
        style={style}
        className={`${radius} object-cover ${className}`}
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <div
      style={{ ...style, fontSize }}
      title={title || name || username || undefined}
      className={`${radius} ${color} flex items-center justify-center text-white font-semibold select-none ${className}`}
      aria-label={alt ?? (name || username || 'Avatar')}
      role="img"
    >
      {initials}
    </div>
  );
};
